import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  ArrowUp, ArrowDown, Eye, EyeOff, Save, RefreshCw,
  Package, Wrench, GripVertical, Home
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AdminHomepageShowcase() {
  const [products, setProducts] = useState([]);
  const [spareParts, setSpareParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('products');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [prodRes, spareRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/products?limit=50`),
        fetch(`${BACKEND_URL}/api/spare-parts`)
      ]);
      if (prodRes.ok) {
        const data = await prodRes.json();
        setProducts(data.sort((a, b) => (a.homepage_order || 0) - (b.homepage_order || 0)));
      }
      if (spareRes.ok) {
        const data = await spareRes.json();
        setSpareParts(data.sort((a, b) => (a.homepage_order || 0) - (b.homepage_order || 0)));
      }
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleHomepage = (list, setList, id) => {
    setList(prev => prev.map(item =>
      item.id === id ? { ...item, show_on_homepage: !item.show_on_homepage } : item
    ));
  };

  const moveItem = (list, setList, id, direction) => {
    const featured = list.filter(i => i.show_on_homepage);
    const idx = featured.findIndex(i => i.id === id);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= featured.length) return;

    const reordered = [...featured];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];

    const orderMap = {};
    reordered.forEach((item, i) => { orderMap[item.id] = i + 1; });

    setList(prev => prev.map(item => ({
      ...item,
      homepage_order: orderMap[item.id] || item.homepage_order || 999
    })).sort((a, b) => {
      if (a.show_on_homepage && !b.show_on_homepage) return -1;
      if (!a.show_on_homepage && b.show_on_homepage) return 1;
      return (a.homepage_order || 0) - (b.homepage_order || 0);
    }));
  };

  const saveProducts = async () => {
    setSaving(true);
    try {
      const items = products.map(p => ({
        id: p.id,
        show_on_homepage: !!p.show_on_homepage,
        homepage_order: p.homepage_order || 0
      }));
      const res = await fetch(`${BACKEND_URL}/api/products/homepage/order`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(items)
      });
      if (res.ok) toast.success('Product homepage order saved!');
      else toast.error('Failed to save');
    } catch { toast.error('Save error'); }
    finally { setSaving(false); }
  };

  const saveSpareParts = async () => {
    setSaving(true);
    try {
      const items = spareParts.map(p => ({
        id: p.id,
        show_on_homepage: !!p.show_on_homepage,
        homepage_order: p.homepage_order || 0
      }));
      const res = await fetch(`${BACKEND_URL}/api/spare-parts/homepage/order`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(items)
      });
      if (res.ok) toast.success('Spare parts homepage order saved!');
      else toast.error('Failed to save');
    } catch { toast.error('Save error'); }
    finally { setSaving(false); }
  };

  const renderList = (items, setItems, type) => {
    const featured = items.filter(i => i.show_on_homepage);
    const notFeatured = items.filter(i => !i.show_on_homepage);

    return (
      <div className="space-y-4">
        {/* Featured on Homepage */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <Home size={14} className="text-blue-600" />
            Showing on Homepage ({featured.length})
          </h3>
          {featured.length === 0 ? (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
              <p className="text-sm text-blue-700">No {type} selected for homepage. Toggle items below to show them.</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {featured.sort((a, b) => (a.homepage_order || 0) - (b.homepage_order || 0)).map((item, idx) => (
                <div key={item.id}
                  className="flex items-center gap-3 bg-white border border-green-200 rounded-xl p-3 shadow-sm"
                  data-testid={`homepage-featured-${item.id}`}>
                  <GripVertical className="text-gray-300 flex-shrink-0" size={16} />
                  <span className="w-6 h-6 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center justify-center flex-shrink-0">
                    {idx + 1}
                  </span>
                  {item.image && (
                    <img src={item.image} alt="" className="w-10 h-10 object-cover rounded-lg border flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-900 truncate">{item.name}</div>
                    <div className="text-xs text-gray-500">{'\u20B9'}{item.price} {item.brand ? `| ${item.brand}` : ''}</div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => moveItem(items, setItems, item.id, -1)} disabled={idx === 0}
                      className="p-1.5 hover:bg-gray-100 rounded-lg disabled:opacity-30" title="Move Up"
                      data-testid={`move-up-${item.id}`}>
                      <ArrowUp size={14} />
                    </button>
                    <button onClick={() => moveItem(items, setItems, item.id, 1)} disabled={idx === featured.length - 1}
                      className="p-1.5 hover:bg-gray-100 rounded-lg disabled:opacity-30" title="Move Down"
                      data-testid={`move-down-${item.id}`}>
                      <ArrowDown size={14} />
                    </button>
                    <button onClick={() => toggleHomepage(items, setItems, item.id)}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" title="Remove from Homepage"
                      data-testid={`remove-from-home-${item.id}`}>
                      <EyeOff size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Available Items */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">
            All {type} ({notFeatured.length} not on homepage)
          </h3>
          <div className="space-y-1.5">
            {notFeatured.map(item => (
              <div key={item.id}
                className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-3 opacity-70 hover:opacity-100 transition-opacity"
                data-testid={`homepage-available-${item.id}`}>
                {item.image && (
                  <img src={item.image} alt="" className="w-10 h-10 object-cover rounded-lg border flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-gray-900 truncate">{item.name}</div>
                  <div className="text-xs text-gray-500">{'\u20B9'}{item.price} {item.brand ? `| ${item.brand}` : ''}</div>
                </div>
                <button onClick={() => toggleHomepage(items, setItems, item.id)}
                  className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs font-medium flex items-center gap-1"
                  data-testid={`add-to-home-${item.id}`}>
                  <Eye size={12} /> Show on Home
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen" data-testid="admin-homepage-showcase">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="showcase-title">Homepage Showcase</h1>
          <p className="text-gray-500 text-sm">Control which products & spare parts appear on homepage and their display order</p>
        </div>
        <button onClick={tab === 'products' ? saveProducts : saveSpareParts} disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
          data-testid="save-showcase-btn">
          {saving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
          Save {tab === 'products' ? 'Products' : 'Spare Parts'} Order
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm mb-4">
        <div className="flex border-b">
          <button onClick={() => setTab('products')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === 'products' ? 'border-blue-600 text-blue-700 bg-blue-50/50' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`} data-testid="tab-products">
            <Package size={16} /> Products
          </button>
          <button onClick={() => setTab('spare-parts')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === 'spare-parts' ? 'border-lime-600 text-lime-700 bg-lime-50/50' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`} data-testid="tab-spare-parts">
            <Wrench size={16} /> Spare Parts
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl p-12 flex items-center justify-center shadow-sm">
          <RefreshCw className="w-6 h-6 text-blue-500 animate-spin" />
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm p-4">
          {tab === 'products'
            ? renderList(products, setProducts, 'products')
            : renderList(spareParts, setSpareParts, 'spare parts')
          }
        </div>
      )}
    </div>
  );
}

export default AdminHomepageShowcase;
